/*
 * <product-gallery>: the mosaic root on the product page. Clicking an image
 * tile opens the product-lightbox dialog named by data-lightbox and fills it
 * from the tile's data-full / data-alt; until data-full is decoded the tile's
 * own loaded photo stands in. Images follow the tiles' DOM order,
 * which is the display order: product-gallery.liquid renders it and
 * product-form.js re-sorts it on variant change, after which the tile labels
 * are renumbered. Inside: previous/next, counter, close on Escape or the close
 * button, and zoom from 1x to 4x. A zoomed photo pans only until one of its
 * edges meets the stage edge.
 * Mouse: click toggles 2.5x at the pointer, the wheel zooms at the pointer,
 * dragging pans while zoomed, a click on the scrim closes.
 * Touch: pinch zooms around the fingers (released below 1x it springs back),
 * one finger pans while zoomed, double-tap toggles 2.5x at the tap, and a
 * single tap does nothing. Unzoomed, a horizontal swipe moves to the
 * previous/next image and a vertical one closes; short swipes spring back.
 * Keys: ←/→ previous/next, or pan with the arrows while zoomed; + or = zooms in
 * a step, - or 0 resets. The zoom button toggles 2.5x at the centre. Focus
 * returns to the tile on close.
 */
if (!customElements.get('product-gallery')) {
  const MAX_SCALE = 4;
  const TOGGLE_SCALE = 2.5;
  const PAN_STEP = 60;
  const PAN_KEYS = { ArrowLeft: [1, 0], ArrowRight: [-1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1] };
  // A press that travels less than SLOP px is a tap or click; a double-tap's second tap lands within DOUBLE_TAP_MS and DOUBLE_TAP_PX.
  const SLOP = 8;
  const DOUBLE_TAP_MS = 300;
  const DOUBLE_TAP_PX = 30;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  customElements.define(
    'product-gallery',
    class extends HTMLElement {
      connectedCallback() {
        new MutationObserver(() => this.renumber()).observe(this, { childList: true });
        this.dialog = document.getElementById(this.dataset.lightbox);
        if (!this.dialog) return;
        this.img = this.dialog.querySelector('[data-lightbox-image]');
        this.stage = this.dialog.querySelector('[data-lightbox-stage]');
        this.counter = this.dialog.querySelector('[data-lightbox-counter]');
        this.zoomButton = this.dialog.querySelector('[data-lightbox-zoom]');
        this.index = 0;
        this.pointers = new Map();

        this.addEventListener('click', (event) => {
          const tile = event.target.closest('[data-full]');
          if (tile) this.openAt(tile);
        });
        this.dialog.addEventListener('click', (event) => this.onClick(event));
        this.dialog.addEventListener('keydown', (event) => this.onKeydown(event));
        this.dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.close();
        });
        this.dialog.addEventListener('close', () => {
          this.dialog.classList.remove('is-open');
          if (this.origin) this.origin.focus();
        });
        this.bindGestures();
      }

      tiles() {
        return Array.from(this.querySelectorAll('[data-full]'));
      }

      /* Tile labels end in the image number; keep it in step when product-form.js re-sorts the figures. */
      renumber() {
        this.tiles().forEach((tile, index) => tile.setAttribute('aria-label', `${this.dataset.tileLabel} — ${index + 1}`));
      }

      openAt(tile) {
        const tiles = this.tiles();
        this.index = Math.max(0, tiles.indexOf(tile));
        this.origin = tile;
        this.pointers.clear();
        this.gesture = null;
        this.lastTap = null;
        this.dialog.toggleAttribute('data-single', tiles.length < 2);
        this.show();
        this.dialog.showModal();
        requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      }

      /* With `from` (px) the photo slides in from that offset: where it sat beside the one swiped away. */
      show(from = 0) {
        const tiles = this.tiles();
        const tile = tiles[this.index];
        if (!tile) return;
        this.resetZoom();
        this.load(tile.dataset.full, tile.querySelector('img'));
        this.img.alt = tile.dataset.alt || '';
        this.counter.textContent = `${this.index + 1} / ${tiles.length}`;
        // Load the neighbours ahead, so a swipe or arrow shows them at once.
        [-1, 1].forEach((delta) => {
          new Image().src = tiles[(this.index + delta + tiles.length) % tiles.length].dataset.full;
        });
        if (!from) return;
        this.pan.x = from;
        this.apply();
        this.img.getBoundingClientRect(); // commit the start position so the slide transitions from it
        this.pan.x = 0;
        this.apply(true);
      }

      /* The full rendition can take seconds on a slow network. If the tile's photo is already loaded on the page,
         it stands in until the full one is decoded, instead of an empty stage or the photo shown before. */
      load(full, preview) {
        const loader = new Image();
        loader.src = full;
        this.loading = loader;
        if (loader.complete || !preview?.complete || !preview.naturalWidth) {
          this.img.src = full;
          return;
        }
        this.img.src = preview.currentSrc;
        loader.decode().then(() => {
          if (this.loading === loader) this.img.src = full;
        }, () => {});
      }

      step(delta, from) {
        const count = this.tiles().length;
        if (count < 2) return;
        this.index = (this.index + delta + count) % count;
        this.show(from);
      }

      close() {
        if (!this.dialog.open) return;
        this.dialog.classList.remove('is-open');
        const duration = parseFloat(getComputedStyle(this.dialog).transitionDuration) * 1000 || 0;
        setTimeout(() => this.dialog.close(), duration);
      }

      /* Touch taps are read from pointer events, and a click that ends a mouse drag is not a click. */
      onClick(event) {
        if (event.target.closest('[data-lightbox-close]')) return this.close();
        if (event.target.closest('[data-lightbox-zoom]')) return this.zoomAt(this.scale > 1 ? 1 : TOGGLE_SCALE);
        const step = event.target.closest('[data-lightbox-step]');
        if (step) return this.step(Number(step.dataset.lightboxStep));
        if (this.pointerType !== 'mouse' || this.dragged) return;
        if (event.target === this.img) return this.zoomAt(this.scale > 1 ? 1 : TOGGLE_SCALE, event.clientX, event.clientY);
        if (event.target === this.stage || event.target === this.dialog) this.close();
      }

      /* Modified keys stay with the browser: Ctrl/Cmd with +, - and 0 is page zoom. */
      onKeydown(event) {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        const pan = PAN_KEYS[event.key];
        if (pan && this.scale > 1) {
          event.preventDefault();
          this.pan.x += pan[0] * PAN_STEP;
          this.pan.y += pan[1] * PAN_STEP;
          this.clampPan();
          return this.apply();
        }
        if (event.key === 'ArrowRight') this.step(1);
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === '+' || event.key === '=') this.zoomAt(this.scale + 0.5);
        if (event.key === '-' || event.key === '0') this.resetZoom(true);
      }

      /* Zoom state lives on the element; the transform is rebuilt from it. pan is the shift of the photo's centre. */
      resetZoom(animate) {
        this.scale = 1;
        this.pan = { x: 0, y: 0 };
        this.apply(animate);
      }

      /* Direct manipulation (drag, pinch, wheel) follows the input; discrete changes ease. */
      apply(animate = false) {
        const { x, y } = this.pan;
        const zoomed = this.scale > 1;
        this.dialog.toggleAttribute('data-instant', !animate);
        this.img.style.transform = this.scale === 1 && !x && !y ? '' : `translate(${x}px, ${y}px) scale(${this.scale})`;
        this.dialog.toggleAttribute('data-zoomed', zoomed);
        if (this.zoomButton) this.zoomButton.setAttribute('aria-pressed', String(zoomed));
      }

      /* The photo's untransformed box in stage coordinates: the on-screen box with the current transform (mid-transition too) divided out. Sub-pixel, unlike offset*. */
      frame() {
        const stage = this.stage.getBoundingClientRect();
        const box = this.img.getBoundingClientRect();
        const { a, d, e, f } = new DOMMatrix(getComputedStyle(this.img).transform);
        return {
          left: stage.left,
          top: stage.top,
          width: stage.width,
          height: stage.height,
          cx: box.left + box.width / 2 - e - stage.left,
          cy: box.top + box.height / 2 - f - stage.top,
          w: box.width / a,
          h: box.height / d,
        };
      }

      /* Keep the stage covered: a photo larger than the stage pans until an edge meets the stage edge; a smaller one stays centred. */
      clampPan(frame = this.frame()) {
        const axis = (pan, size, stage, centre) => {
          const nearEdge = (this.scale * size) / 2 - centre;
          const farEdge = stage - centre - (this.scale * size) / 2;
          return farEdge <= nearEdge ? clamp(pan, farEdge, nearEdge) : clamp(0, nearEdge, farEdge);
        };
        this.pan = { x: axis(this.pan.x, frame.w, frame.width, frame.cx), y: axis(this.pan.y, frame.h, frame.height, frame.cy) };
      }

      /* Scale about a viewport point (the stage centre without one), keeping the photo point under it in place as far as the edges allow. */
      zoomAt(scale, x, y, animate = true) {
        const next = Math.min(MAX_SCALE, scale);
        if (next <= 1) return this.resetZoom(animate);
        const frame = this.frame();
        const ax = x === undefined ? frame.width / 2 - frame.cx : x - frame.left - frame.cx;
        const ay = y === undefined ? frame.height / 2 - frame.cy : y - frame.top - frame.cy;
        const k = next / this.scale;
        this.pan = { x: ax - k * (ax - this.pan.x), y: ay - k * (ay - this.pan.y) };
        this.scale = next;
        this.clampPan(frame);
        this.apply(animate);
      }

      bindGestures() {
        this.stage.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.stage.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.stage.addEventListener('pointerup', (event) => this.onPointerEnd(event));
        // WebKit reports 0,0 on pointercancel: end from the last known position and never treat it as a tap or a swipe.
        this.stage.addEventListener('pointercancel', (event) => this.onPointerEnd(event, true));
        this.stage.addEventListener('wheel', (event) => {
          event.preventDefault();
          this.zoomAt(this.scale - event.deltaY * 0.002, event.clientX, event.clientY, false);
        }, { passive: false });
        // Safari's own pinch events would zoom the page behind the dialog.
        ['gesturestart', 'gesturechange'].forEach((type) => this.dialog.addEventListener(type, (event) => event.preventDefault()));
      }

      /* Each gesture is measured from where it began, so every frame is computed afresh instead of accumulated. */
      begin(mode) {
        if (mode === 'pinch') this.clampPan();
        const [a, b] = this.pointers.values();
        this.gesture = { mode, x: a.x, y: a.y, pan: { ...this.pan }, scale: this.scale, time: performance.now() };
        if (mode === 'pinch') Object.assign(this.gesture, { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, spread: Math.hypot(b.x - a.x, b.y - a.y) || 1 });
      }

      onPointerDown(event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.isPrimary) {
          this.pointers.clear();
          this.pointerType = event.pointerType;
          this.dragged = false;
        }
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (this.pointers.size === 1) this.begin('press');
        else if (this.pointers.size === 2) this.begin('pinch');
      }

      onPointerMove(event) {
        const point = this.pointers.get(event.pointerId);
        const gesture = this.gesture;
        if (!point || !gesture) return;
        point.x = event.clientX;
        point.y = event.clientY;
        if (gesture.mode === 'pinch') return this.pinch();
        const dx = point.x - gesture.x;
        const dy = point.y - gesture.y;
        if (gesture.mode === 'press') {
          if (Math.hypot(dx, dy) < SLOP) return;
          gesture.mode = this.dragMode(dx, dy);
          this.dragged = true;
          this.stage.setPointerCapture(event.pointerId);
        }
        if (gesture.mode === 'pan') {
          this.pan = { x: gesture.pan.x + dx, y: gesture.pan.y + dy };
          this.clampPan();
        } else if (gesture.mode === 'swipe-x') this.pan = { x: dx, y: 0 };
        else if (gesture.mode === 'swipe-y') this.pan = { x: 0, y: dy };
        else return;
        this.apply();
      }

      /* A zoomed photo pans. Unzoomed, touch swipes between images or towards closing; a mouse drag does nothing. */
      dragMode(dx, dy) {
        if (this.scale > 1) return 'pan';
        if (this.pointerType === 'mouse') return 'idle';
        if (Math.abs(dy) >= Math.abs(dx)) return 'swipe-y';
        return this.tiles().length > 1 ? 'swipe-x' : 'idle';
      }

      /* The photo point first under the fingers' midpoint follows the midpoint; the scale follows their spread. */
      pinch() {
        const gesture = this.gesture;
        const [a, b] = this.pointers.values();
        const frame = this.frame();
        // Below 1x is allowed while pinching (down to 0.5x) and springs back on release.
        const scale = clamp((gesture.scale * Math.hypot(b.x - a.x, b.y - a.y)) / gesture.spread, 0.5, MAX_SCALE);
        const k = scale / gesture.scale;
        const start = { x: gesture.x - frame.left - frame.cx, y: gesture.y - frame.top - frame.cy };
        const mid = { x: (a.x + b.x) / 2 - frame.left - frame.cx, y: (a.y + b.y) / 2 - frame.top - frame.cy };
        this.scale = scale;
        this.pan = { x: mid.x - k * (start.x - gesture.pan.x), y: mid.y - k * (start.y - gesture.pan.y) };
        this.clampPan(frame);
        this.apply();
      }

      onPointerEnd(event, cancelled = false) {
        if (!this.pointers.delete(event.pointerId) || !this.gesture) return;
        const gesture = this.gesture;
        if (gesture.mode === 'pinch') {
          if (this.pointers.size > 1) return this.begin('pinch');
          // Within 5% of 1x counts as 1x: two-finger taps and hesitant pinches leave no stray zoom.
          if (this.scale < 1.05) this.resetZoom(true);
          if (this.pointers.size) return this.begin(this.scale > 1 ? 'pan' : 'idle');
        }
        if (this.pointers.size) return;
        this.gesture = null;
        if (gesture.mode === 'press' && !cancelled && this.pointerType !== 'mouse') this.tap(gesture.x, gesture.y);
        if (gesture.mode === 'swipe-x' || gesture.mode === 'swipe-y') this.endSwipe(gesture, cancelled);
      }

      /* A single tap does nothing, so a stray touch never zooms; a second one soon after and nearby toggles zoom there. */
      tap(x, y) {
        const last = this.lastTap;
        const now = performance.now();
        if (last && now - last.time < DOUBLE_TAP_MS && Math.hypot(x - last.x, y - last.y) < DOUBLE_TAP_PX) {
          this.lastTap = null;
          return this.zoomAt(this.scale > 1 ? 1 : TOGGLE_SCALE, x, y);
        }
        this.lastTap = { x, y, time: now };
      }

      /* A quarter of the stage, or a quick flick, commits; anything less springs back. */
      endSwipe(gesture, cancelled) {
        const horizontal = gesture.mode === 'swipe-x';
        const distance = horizontal ? this.pan.x : this.pan.y;
        const frame = this.frame();
        const size = horizontal ? frame.width : frame.height;
        const flick = Math.abs(distance) > SLOP * 4 && Math.abs(distance) / (performance.now() - gesture.time) > 0.5;
        if (cancelled || (Math.abs(distance) < size / 4 && !flick)) return this.resetZoom(true);
        const direction = Math.sign(distance);
        if (horizontal) return this.step(-direction, distance - direction * frame.width);
        this.pan.y = (direction * size) / 2;
        this.apply(true);
        this.close();
      }
    }
  );
}
